import { useId, useRef, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { IconButton } from './Button'
import { useOverlay } from './overlay'

export interface DrawerProps {
  open: boolean
  onClose: () => void
  title: ReactNode
  /** A line under the title: channel, status, slot. */
  meta?: ReactNode
  children?: ReactNode
  /** The action bar, pinned to the bottom. */
  footer?: ReactNode
  /** `right` is a detail panel (480px from 1024px up, the whole screen below);
   *  `bottom` is a sheet for the phone's "more" menu. */
  side?: 'right' | 'bottom'
  className?: string
}

/** A detail view over the list it came from. The route owns `open`, so the
 *  URL changes with it and a reload or a shared link lands on the same item. */
export function Drawer({ open, onClose, title, meta, children, footer, side = 'right', className }: DrawerProps) {
  const panel = useRef<HTMLDivElement>(null)
  const titleId = useId()
  useOverlay(open, onClose, panel, 'panel')
  if (!open) return null
  const right = side === 'right'
  return createPortal(
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} aria-hidden />
      <div
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className={cn(
          'absolute flex flex-col bg-card text-card-foreground shadow-overlay outline-none',
          right
            ? 'inset-0 animate-drawer-in lg:inset-y-0 lg:left-auto lg:right-0 lg:w-drawer lg:border-l lg:border-border'
            : 'inset-x-0 bottom-0 max-h-[85vh] animate-sheet-in rounded-t-2xl border-t border-border pb-[env(safe-area-inset-bottom)]',
          className,
        )}
      >
        {!right && <div className="mx-auto mt-2 h-1 w-9 rounded-full bg-border" aria-hidden />}
        <header className="flex items-start gap-3 border-b border-border px-4 py-3 lg:px-5">
          <div className="min-w-0 flex-1">
            <h2 id={titleId} className="truncate text-section font-semibold">
              {title}
            </h2>
            {meta && <div className="mt-1 flex flex-wrap items-center gap-2 text-meta text-muted-foreground">{meta}</div>}
          </div>
          <IconButton label="닫기" icon={X} onClick={onClose} className="-mr-2" />
        </header>
        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4 lg:px-5">{children}</div>
        {footer && (
          <footer className="flex flex-wrap items-center gap-2 border-t border-border bg-card px-4 py-3 lg:px-5">
            {footer}
          </footer>
        )}
      </div>
    </div>,
    document.body,
  )
}
